/**
 * Drawer.
 *
 * The overlay primitive for the cart, search, mobile menu and collection filters. All
 * four are the same object with different contents, so they share one implementation.
 *
 * Built on the native <dialog> element and `showModal()` rather than a div with a
 * hand-rolled trap. That gives focus containment, Escape-to-close, background inertness
 * and top-layer stacking from the platform — four things themes routinely get wrong —
 * and leaves this file responsible only for animation, scroll lock and focus return.
 *
 * Markup contract:
 *
 *   <button data-drawer-open="CartDrawer" aria-haspopup="dialog">Cart</button>
 *
 *   <drawer-element id="CartDrawer">
 *     <dialog class="drawer" scroll-lock aria-label="Cart">
 *       <div class="drawer__panel">
 *         <button data-drawer-close>Close</button>
 *         ...
 *       </div>
 *     </dialog>
 *   </drawer-element>
 *
 * `data-side` on <drawer-element> may be `end` (default), `start` or `bottom`.
 */

class DrawerElement extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.markOpeners();

    /* Delegated at the document rather than bound only to the openers found above: an
       opener can exist in markup that has not parsed yet when this element connects —
       the mobile dock's triggers render in the overlay group, after the cart and search
       drawers that precede it there and after `sections/header.liquid`'s own MenuDrawer
       — and a listener attached only to today's NodeList would never see one added
       later. Delegation means any opener works the moment it exists, regardless of where
       it sits. */
    document.addEventListener('click', this.onDocumentClick);

    this.querySelectorAll('[data-drawer-close]').forEach((closer) =>
      closer.addEventListener('click', () => this.close())
    );

    // Clicking the backdrop closes. The dialog element itself is the backdrop; the
    // panel inside it is what the pointer lands on for any click that should not close.
    this.dialog.addEventListener('click', this.onDialogClick);

    // `cancel` fires on Escape. Intercepted so the close animation can play before the
    // dialog is removed from the top layer.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  /* Best-effort, run once at connect: labels whatever openers already exist so a screen
     reader has `aria-haspopup` from load rather than only after a first click. An opener
     that appears later still opens the drawer via delegation below, and is labelled the
     moment that click lands. */
  markOpeners() {
    document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((opener) => {
      opener.setAttribute('aria-haspopup', 'dialog');
    });
  }

  /* Only openers that already declare `aria-expanded` are kept in step with the dialog.
     The attribute is never added here: `aria-haspopup="dialog"` is the whole contract for
     a button that opens a modal, and a button that does not claim to expand anything
     should not start. What this prevents is the opposite failure — an authored
     `aria-expanded="false"` that never moves, which tells a screen reader the drawer is
     shut while it is open on screen. */
  syncOpeners(expanded) {
    document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((opener) => {
      if (!opener.hasAttribute('aria-expanded')) return;
      opener.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  onDocumentClick = (event) => {
    const opener = event.target.closest(`[data-drawer-open="${this.id}"]`);
    if (!opener) return;
    opener.setAttribute('aria-haspopup', 'dialog');
    event.preventDefault();
    this.open(opener);
  };

  onDialogClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  get isOpen() {
    return this.dialog?.open ?? false;
  }

  open(trigger) {
    if (!this.dialog || this.isOpen) return;

    this.trigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
    this.dialog.showModal();
    this.dialog.setAttribute('data-state', 'open');
    this.syncOpeners(true);
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.dialog || !this.isOpen) return;

    const finish = () => {
      this.dialog.close();
      this.dialog.removeAttribute('data-state');
      this.syncOpeners(false);
      // Focus returns to whatever opened the drawer, so a keyboard user resumes where
      // they left off rather than at the top of the document.
      if (this.trigger instanceof HTMLElement && this.trigger.isConnected) this.trigger.focus();
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    };

    this.dialog.setAttribute('data-state', 'closing');

    // Wait for the panel's transition so the drawer slides out rather than vanishing.
    // The timeout is a fallback for the case where motion is off and no event fires.
    const panel = this.dialog.querySelector('.drawer__panel') ?? this.dialog;
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      panel.removeEventListener('transitionend', done);
      finish();
    };

    panel.addEventListener('transitionend', done);
    setTimeout(done, 700);
  }
}

if (!customElements.get('drawer-element')) {
  customElements.define('drawer-element', DrawerElement);
}
